import { createBrowserRouter, Navigate } from "react-router-dom";

import { AuthProvider } from "@covertree/auth";
import {
  createAuthRoutes,
  ProtectedRoute,
  AUTH_ROUTES,
  BASE_AUTH_PATH,
} from "@covertree/routing";
import { Maple } from "@covertree/ui/brand/Logo";
import PageLayout from "@covertree/layouts/PageLayout";
import { BannerPortalName } from "@covertree/ui";

import { CognitoUserGroup } from "@enums";

import { resetCache } from "@redux/store";

import Dashboard, { BASE_DASHBOARD_PATH } from "@routing/Dashboard";

import Search from "@routing/Residents/components/Search";

import Residents, {
  BASE_RESIDENTS_PATH,
  RESIDENTS_ROUTES,
} from "@routing/Residents";

import ReviewDetails from "@routing/Residents/ResidentsContent/ReviewDetails";
import ResidentDetails from "@routing/Residents/ResidentsContent/ResidentDetails";

import ProofOfCoverage, {
  ADD_EVENT_ROUTES,
  BASE_COVERAGE_PATH,
  BASE_PROOF_OF_COVERAGE_PATH,
  COVERAGE_ROUTES,
  PROOF_OF_COVERAGE_ROUTES,
} from "@routing/Residents/ProofOfCoverage";

import PolicyType from "@routing/Residents/ProofOfCoverage/PolicyType";
import PolicyDocument from "@routing/Residents/ProofOfCoverage/PolicyDocument";
import ScheduledTllFound from "@routing/Residents/ProofOfCoverage/ScheduledTllFound";
import ActiveTLLFound from "@routing/Residents/ProofOfCoverage/ActiveTLLFound";
import CoverageAdded from "@routing/Residents/ProofOfCoverage/CoverageAdded";
import { ExternalCoverageDocuments } from "@routing/Residents/ProofOfCoverage/ExternalCoverageDocuments";
import CoverageDetails from "@routing/Residents/ProofOfCoverage/CoverageData/ProofOfCoverageDetails";
import ProofOfCoverageForm from "@routing/Residents/ProofOfCoverage/CoverageData/ProofOfCoverageForm";
import PolicyAlreadyAdded from "@routing/Residents/ProofOfCoverage/CoverageData/PolicyAlreadyAdded";
import PolicyNotFound from "@routing/Residents/ProofOfCoverage/CoverageData/PolicyNotFound";

import {
  BASE_COVERAGE_DATA_PATH,
  COVERAGE_DATA_ROUTES,
} from "@routing/Residents/ProofOfCoverage/CoverageData";

import { getPageTitle, featureFlags } from "@utils";
import { CoverageEventDetails } from "@routing/Residents/CoverageEventDetails/CoverageEventDetails";
import { AddCoverageEvent } from "@routing/Residents/AddCoverageEvent/AddCoverageEvent";
import { EventType } from "@routing/Residents/AddCoverageEvent/EventType/EventType";
import { EventSummary } from "@routing/Residents/AddCoverageEvent/EventSummary/EventSummary";
import { EventAdded } from "@routing/Residents/AddCoverageEvent/EventAdded/EventAdded";
import {
  ADD_PORTFOLIO_ROUTES,
  BASE_PORTFOLIOS_PATH,
  PORTFOLIO_ROUTES,
  PORTFOLIOS_ROUTES,
} from "@routing/Portfolios";
import AccessPatternProvider from "../providers/AccessPatternProvider";
import Portfolios from "@routing/Portfolios/Portfolios";
import { PortfolioDetails } from "@routing/Portfolios/PortfolioDetails/PortfolioDetails";
import { AddPortfolio } from "@routing/Portfolios/AddPortfolio/AddPortfolio";
import { PortfolioConfiguration } from "@routing/Portfolios/AddPortfolio/PortfolioConfiguration/PortfolioConfiguration";
import { CommunitiesConfiguration } from "@routing/Portfolios/AddPortfolio/CommunitiesConfiguration/CommunitiesConfiguration";
import { PortfolioConfigurationSuccess } from "@routing/Portfolios/AddPortfolio/PortfolioConfiguration/PortfolioConfigurationSuccess";

const mapleContactSection = {
  name: "",
  email: "decpage@covertree.com",
  phone: "(205) 961-2511",
};

export const router = () =>
  createBrowserRouter([
    {
      path: "*",
      element: (
        <AuthProvider
          authFlowType="CUSTOM_WITHOUT_SRP"
          confirmSignInPath={`${BASE_AUTH_PATH}/${AUTH_ROUTES.CONFIRM_SIGN_IN}`}
          onSignOut={resetCache}
        />
      ),
      children: [
        {
          index: true,
          element: <Navigate to={`${BASE_AUTH_PATH}/${AUTH_ROUTES.LOGIN}`} />,
        },
        // Login related routes
        ...createAuthRoutes({
          confirmSignInPage: {
            title: getPageTitle("Confirm sign in"),
          },
          loginPage: {
            title: getPageTitle("Sign In"),
          },
          ssoProvidersList: [],
          isPasswordless: true,
          isMFAEnabled: false,
          whenLoggedIn: (groups) => {
            if (groups.includes(CognitoUserGroup.Resident)) {
              return `/${BASE_DASHBOARD_PATH}`;
            }
            return `/${BASE_RESIDENTS_PATH}`;
          },
          logo: <Maple height={100} width={86} />,
        }),
        // Executives / service agents routes
        {
          path: "*",
          element: (
            <ProtectedRoute
              allowedGroups={[
                CognitoUserGroup.CommunityManager,
                CognitoUserGroup.CommunityPortfolioExecutive,
                CognitoUserGroup.CoverTreeEmployee,
                CognitoUserGroup.CoverTreeExecutive,
              ]}
              shouldBeLoggedIn={true}
              whenNotAllowed={() => `/${BASE_AUTH_PATH}`}>
              <AccessPatternProvider>
                {({ canSeePortfolioList }) => (
                  <PageLayout
                    enableBanner={featureFlags.banners.isEnabled()}
                    bannerPortalName={BannerPortalName.Maple}
                    navigationItems={[
                      ...(canSeePortfolioList
                        ? [
                            {
                              name: "Portfolios",
                              path: `/${BASE_PORTFOLIOS_PATH}`,
                            },
                          ]
                        : []),
                      { name: "Residents", path: `/${BASE_RESIDENTS_PATH}` },
                    ]}
                    search={<Search />}
                    logo={<Maple height={100} width={86} />}
                    contacts={[mapleContactSection]}
                  />
                )}
              </AccessPatternProvider>
            </ProtectedRoute>
          ),
          children: [
            {
              path: BASE_RESIDENTS_PATH,
              element: <Residents />,
              children: [
                {
                  path: `${RESIDENTS_ROUTES.RESIDENT}/:residentId`,
                  element: <ResidentDetails />,
                  children: [
                    {
                      path: BASE_PROOF_OF_COVERAGE_PATH,
                      element: <ProofOfCoverage />,
                      children: [
                        {
                          path: PROOF_OF_COVERAGE_ROUTES.POLICY_TYPE,
                          element: <PolicyType />,
                        },
                        {
                          path: PROOF_OF_COVERAGE_ROUTES.POLICY_DOCUMENT,
                          element: <PolicyDocument />,
                        },
                        {
                          path: PROOF_OF_COVERAGE_ROUTES.SCHEDULED_TLL_FOUND,
                          element: <ScheduledTllFound />,
                        },
                        {
                          path: PROOF_OF_COVERAGE_ROUTES.ACTIVE_TLL_FOUND,
                          element: <ActiveTLLFound />,
                        },
                        {
                          path: PROOF_OF_COVERAGE_ROUTES.COVERAGE_ADDED,
                          element: <CoverageAdded />,
                        },
                        {
                          path: PROOF_OF_COVERAGE_ROUTES.COVERAGE_DOCUMENTS,
                          element: (
                            <ExternalCoverageDocuments isAddingEvent={false} />
                          ),
                        },
                        {
                          path: BASE_COVERAGE_DATA_PATH,
                          children: [
                            {
                              path: COVERAGE_DATA_ROUTES.COVERAGE_DETAILS,
                              element: <CoverageDetails />,
                            },
                            {
                              path: COVERAGE_DATA_ROUTES.EXTERNAL_COVERAGE,
                              element: (
                                <ProofOfCoverageForm isAddingEvent={false} />
                              ),
                            },
                            {
                              path: COVERAGE_DATA_ROUTES.POLICY_ALREADY_ADDED,
                              element: <PolicyAlreadyAdded />,
                            },
                            {
                              path: COVERAGE_DATA_ROUTES.POLICY_NOT_FOUND,
                              element: <PolicyNotFound />,
                            },
                          ],
                        },
                        {
                          path: "*",
                          element: <Navigate to={BASE_RESIDENTS_PATH} />,
                        },
                      ],
                    },
                    {
                      path: `${BASE_COVERAGE_PATH}/:coverageId/${COVERAGE_ROUTES.ADD_EVENT}`,
                      element: <AddCoverageEvent />,
                      children: [
                        {
                          path: ADD_EVENT_ROUTES.EVENT_TYPE,
                          element: <EventType />,
                        },
                        {
                          path: ADD_EVENT_ROUTES.EVENT_DETAILS,
                          element: <ProofOfCoverageForm isAddingEvent />,
                        },
                        {
                          path: ADD_EVENT_ROUTES.EVENT_DOCUMENTS,
                          element: <ExternalCoverageDocuments isAddingEvent />,
                        },
                        {
                          path: ADD_EVENT_ROUTES.EVENT_SUMMARY,
                          element: <EventSummary />,
                        },
                        {
                          path: ADD_EVENT_ROUTES.EVENT_ADDED,
                          element: <EventAdded />,
                        },
                      ],
                    },
                    {
                      path: `${BASE_COVERAGE_PATH}/:coverageId/${COVERAGE_ROUTES.EVENT}/:eventId`,
                      element: <CoverageEventDetails />,
                    },
                  ],
                },
                {
                  path: `${RESIDENTS_ROUTES.REVIEW}/:residentId`,
                  element: <ReviewDetails />,
                },
              ],
            },
            {
              path: BASE_PORTFOLIOS_PATH,
              element: (
                <ProtectedRoute
                  allowedGroups={[CognitoUserGroup.CoverTreeExecutive]}
                  shouldBeLoggedIn={true}
                  whenNotAllowed={() => `/${BASE_AUTH_PATH}`}>
                  <Portfolios />
                </ProtectedRoute>
              ),
              children: [
                {
                  path: `${PORTFOLIOS_ROUTES.PORTFOLIO}/:portfolioId`,
                  element: <PortfolioDetails />,
                },
                {
                  path: `${PORTFOLIOS_ROUTES.PORTFOLIO}/${PORTFOLIO_ROUTES.ADD}`,
                  element: <AddPortfolio />,
                  children: [
                    {
                      path: ADD_PORTFOLIO_ROUTES.CONFIGURATION,
                      element: <PortfolioConfiguration />,
                    },
                    {
                      path: ADD_PORTFOLIO_ROUTES.CONFIGURATION_SUCCESS,
                      element: <PortfolioConfigurationSuccess />,
                    },
                    {
                      path: `${ADD_PORTFOLIO_ROUTES.COMMUNITIES}/:portfolioId`,
                      element: <CommunitiesConfiguration />,
                    },
                  ],
                },
              ],
            },
            {
              path: "*",
              element: <Navigate to={BASE_RESIDENTS_PATH} />,
            },
          ],
        },
        // Residents routes
        {
          path: "*",
          element: (
            <ProtectedRoute
              allowedGroups={[CognitoUserGroup.Resident]}
              shouldBeLoggedIn={true}
              whenNotAllowed={() => BASE_AUTH_PATH}>
              <PageLayout
                navigationItems={[]}
                logo={<Maple height={100} width={86} />}
                contacts={[mapleContactSection]}
              />
            </ProtectedRoute>
          ),
          children: [
            {
              path: BASE_DASHBOARD_PATH,
              element: <Dashboard />,
            },
            {
              path: "*",
              element: <Navigate to={BASE_DASHBOARD_PATH} />,
            },
          ],
        },
      ],
    },
  ]);
